import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import { Appointment, Notification, AppointmentSeriesListItem } from '../types';
import NavigationBar from '../components/NavigationBar';
import PermissionGate from '../components/PermissionGate';
import LoadingSpinner from '../components/LoadingSpinner';
import { Calendar, Clock, User, Mail, Phone, MapPin, FileText, CheckCircle, XCircle, AlertCircle, AlertTriangle, Clock as ClockIcon, X, Save, ArrowLeft, Trash2, Heart, Settings, CalendarClock, ListOrdered, HelpCircle, Repeat2 } from 'lucide-react';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { canViewFullCustomerPhone, getDashboardRoute } from '../utils/roleUtils';
import { displayCustomerPhone } from '../utils/phoneFormatUtils';
import { getDashboardTitle, shouldShowBusinessDashboard } from '../utils/navigationUtils';
import BusinessDashboardSidebar from '../components/BusinessDashboardSidebar';
import AppointmentActivitySection from '../components/AppointmentActivitySection';
import MarkDepositPaidModal from '../components/MarkDepositPaidModal';
import DepositPaymentDetailsModal from '../components/DepositPaymentDetailsModal';
import {
  getServiceNames,
  getTotalDuration,
  getTotalPrice,
  getProfessionalNames,
  getFirstProfessionalInitials,
  getFirstProfessionalEmail,
  formatAppointmentStatusLabel,
  appointmentDetailsStatusBadgeClass,
  isPendingDepositOutstanding,
  isDepositOutstanding,
  getDepositAmountToRecord,
} from '../utils/appointmentHelpers';

function renderCustomerBookingNoteContent(notes: string): React.ReactNode {
  return notes.split(/\n\n/).map((section, index) => {
    const preferredMatch = section.match(/^Preferred:\s*([\s\S]*)$/);
    if (preferredMatch) {
      const body = preferredMatch[1];
      return (
        <div key={index} className={index > 0 ? 'mt-3' : undefined}>
          <span className="font-semibold text-gray-900">Preferred:</span>
          <br />
          {body ? <span className="whitespace-pre-wrap">{body}</span> : null}
        </div>
      );
    }
    return (
      <div key={index} className={`whitespace-pre-wrap ${index > 0 ? 'mt-3' : ''}`}>
        {section}
      </div>
    );
  });
}

const AppointmentDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const { user } = useAuth();
  const { currentBusiness } = useBusiness();
  
  // Sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<'not_found' | 'unauthorized' | 'server_error' | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDurationModalOpen, setIsDurationModalOpen] = useState(false);
  const [durationEditing, setDurationEditing] = useState<{
    assignmentId: string;
    serviceName: string;
    startIso: string;
    currentDurationMinutes: number;
  } | null>(null);
  const [durationHours, setDurationHours] = useState<string>('0');
  const [durationMinutes, setDurationMinutes] = useState<string>('00');
  const [isSavingDuration, setIsSavingDuration] = useState(false);
  
  // Notification state
  const [unreadNotifications, setUnreadNotifications] = useState<Notification[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const notificationsLoadedRef = useRef(false);
  
  // Private professional notes state
  const [privateNotes, setPrivateNotes] = useState<string>('');
  const [isEditingPrivateNotes, setIsEditingPrivateNotes] = useState(false);
  const [privateNotesLoading, setPrivateNotesLoading] = useState(false);
  const [privateNotesSaving, setPrivateNotesSaving] = useState(false);
  
  // Actions dropdown state
  const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false);
  const actionsDropdownDesktopRef = useRef<HTMLDivElement>(null);
  const actionsDropdownMobileRef = useRef<HTMLDivElement>(null);
  
  // Business info state
  const [businessInfo, setBusinessInfo] = useState<any>(null);
  const [operatingHours, setOperatingHours] = useState<any[]>([]);
  const [businessServices, setBusinessServices] = useState<any[]>([]);
  const [businessSettings, setBusinessSettings] = useState<{ showProfessionalNameOnAssignment: boolean } | null>(null);
  const [businessProfessionalsCount, setBusinessProfessionalsCount] = useState<number>(0);

  // Assignment modal state
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<any>(null);
  const [selectedProfessionalId, setSelectedProfessionalId] = useState<string>('');
  const [assignmentProfessionals, setAssignmentProfessionals] = useState<any[]>([]);
  const [assignmentCapabilities, setAssignmentCapabilities] = useState<{[professionalId: string]: any[]}>({});
  const [assignmentSchedules, setAssignmentSchedules] = useState<{[professionalId: string]: any[]}>({});
  const [assignmentExistingAppointments, setAssignmentExistingAppointments] = useState<{[professionalId: string]: Appointment[]}>({});
  const [loadingAssignmentData, setLoadingAssignmentData] = useState(false);
  const [isSavingAssignment, setIsSavingAssignment] = useState(false);

  const openDurationModalForAssignment = (assignment: any) => {
    if (!appointment) return;
    if (!assignment?.id) return;
    if (appointment.status === 'CANCELLED' || appointment.status === 'COMPLETED') return;
    if (isUpdating) return;

    const startIso = String(assignment.fromDateTime || appointment.scheduledAt || appointment.flexScheduledAt?.earliest || '');
    const cur = Math.max(0, Number(assignment.durationMinutes) || 0);
    const rounded = Math.max(15, Math.round(cur / 15) * 15);
    const h = Math.floor(rounded / 60);
    const m = rounded % 60;

    setDurationEditing({
      assignmentId: String(assignment.id),
      serviceName: String(assignment.serviceName || 'Service'),
      startIso,
      currentDurationMinutes: cur
    });
    setDurationHours(String(h));
    setDurationMinutes(String(m).padStart(2, '0'));
    setIsDurationModalOpen(true);
  };

  const closeDurationModal = () => {
    if (isSavingDuration) return;
    setIsDurationModalOpen(false);
    setDurationEditing(null);
  };

  // Appointment Summary collapse state (collapsed by default in mobile)
  const [isAppointmentSummaryCollapsed, setIsAppointmentSummaryCollapsed] = useState(true);

  // Activity section refetch trigger (increment after mutations)
  const [activityRefreshTrigger, setActivityRefreshTrigger] = useState(0);

  useEffect(() => {
    // Load appointment data from API
    const loadAppointment = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!appointmentId) {
          throw new Error('Appointment ID is required');
        }
        
        const appointmentData = await apiService.getAppointment(appointmentId);
        setAppointment(appointmentData);
        setNotesText(appointmentData.notes || '');
        
        // Load private notes if we have customer and business (async, don't block)
        if (appointmentData.customerId && currentBusiness?.id) {
          apiService.getCustomerPrivateNotes(appointmentData.customerId, currentBusiness.id)
            .then(notesData => setPrivateNotes(notesData.notes || ''))
            .catch(error => {
              console.error('Error loading private notes:', error);
              setPrivateNotes('');
            });
        }
        
        // Load business info (async, don't block)
        if (appointmentData.business_short_name) {
          apiService.getBusiness(appointmentData.business_short_name)
            .then(businessData => {
              // The API returns { business: {...}, services: [...], operatingHours: [...] }
              // Extract the business object
              const business = businessData?.business || businessData;
              setBusinessInfo(business);
              setBusinessServices((businessData as any)?.services || []);
              
              // Use operating hours from the API response if available, otherwise fetch separately
              if (businessData?.operatingHours && Array.isArray(businessData.operatingHours)) {
                setOperatingHours(businessData.operatingHours);
              } else {
                // Load operating hours using businessId from appointment or business object
                const businessId = appointmentData.businessId || business?.id;
                if (businessId) {
                  apiService.getOperatingHours(businessId)
                    .then(hours => setOperatingHours(hours || []))
                    .catch(error => console.error('Error loading operating hours:', error));
                }
              }
            })
            .catch(error => console.error('Error loading business info:', error));
        } else if (appointmentData.businessId) {
          // Fallback: try to load operating hours directly if we have businessId
          apiService.getOperatingHours(appointmentData.businessId)
            .then(hours => setOperatingHours(hours || []))
            .catch(error => console.error('Error loading operating hours:', error));
          // Also fetch services for service location display
          apiService.getBusinessServices(appointmentData.businessId)
            .then(services => setBusinessServices(services || []))
            .catch(error => console.error('Error loading business services:', error));
        }
      } catch (err: any) {
        console.error('Error loading appointment:', err);
        
        // Handle different error types
        if (err.response?.status === 404) {
          setErrorType('not_found');
          setError('Appointment not found');
        } else if (err.response?.status === 401 || err.response?.status === 403) {
          setErrorType('unauthorized');
          setError(err.response?.status === 401 ? 'Please log in to view this appointment' : 'You do not have permission to view this appointment');
        } else {
          setErrorType('server_error');
          setError(err.response?.data?.message || 'Failed to load appointment details');
        }
      } finally {
        setLoading(false);
      }
    };

    if (appointmentId) {
      loadAppointment();
    }
  }, [appointmentId, currentBusiness?.id]);

  // Load notifications function
  const loadNotifications = useCallback(async () => {
    if (!currentBusiness?.id) return;
    
    setNotificationsLoading(true);
    try {
      const response = await apiService.getNotifications(currentBusiness.id);
      const unread = response.data.filter((notification: Notification) => !notification.is_read);
      setUnreadNotifications(unread);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setNotificationsLoading(false);
    }
  }, [currentBusiness?.id]);

  // Load unread notifications count for navbar badge
  const loadUnreadCount = useCallback(async () => {
    if (!currentBusiness?.id) return;
    try {
      // Make a lightweight call with limit 1 just to get the total count from pagination
      const response = await apiService.getNotifications(currentBusiness.id, { page: 1, limit: 1, isRead: false });
      // Use pagination.total for the actual count of ALL unread notifications
      const totalUnread = response.pagination?.total || 0;
      // Update unreadNotifications with the correct count
      setUnreadNotifications(Array(totalUnread).fill(null) as Notification[]);
    } catch (error) {
      console.error('Error loading unread notifications count:', error);
      setUnreadNotifications([]);
    }
  }, [currentBusiness?.id]);

  // Handle notification bell click
  const handleNotificationsClick = () => {
    navigate('/dashboard/business?section=notifications');
  };

  // Load unread count on component mount to show badge
  useEffect(() => {
    if (currentBusiness?.id && !notificationsLoading && !notificationsLoadedRef.current) {
      loadUnreadCount();
      notificationsLoadedRef.current = true;
    }
  }, [currentBusiness?.id, loadUnreadCount, notificationsLoading]);

  // Bump trigger so AppointmentActivitySection refetches after mutations
  const refreshActivityEvents = useCallback(() => {
    setActivityRefreshTrigger(c => c + 1);
  }, []);

  // Load business settings and professionals count for "show professional name" logic
  useEffect(() => {
    if (!currentBusiness?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const [settingsRes, profsRes] = await Promise.all([
          apiService.getBusinessSettings(currentBusiness.id),
          apiService.getBusinessProfessionalsDetailed(currentBusiness.id).catch(() => [])
        ]);
        if (cancelled) return;
        setBusinessSettings({ showProfessionalNameOnAssignment: settingsRes?.showProfessionalNameOnAssignment ?? false });
        setBusinessProfessionalsCount(Array.isArray(profsRes) ? profsRes.length : 0);
      } catch {
        if (!cancelled) {
          setBusinessSettings({ showProfessionalNameOnAssignment: false });
          setBusinessProfessionalsCount(0);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [currentBusiness?.id]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CONFIRMED': return 'bg-green-100 text-green-800';
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      case 'COMPLETED': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'CONFIRMED': return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'PENDING': return <AlertCircle className="h-5 w-5 text-yellow-600" />;
      case 'CANCELLED': return <XCircle className="h-5 w-5 text-red-600" />;
      case 'COMPLETED': return <CheckCircle className="h-5 w-5 text-blue-600" />;
      default: return <ClockIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDateOnly = (dateString: string | null | undefined) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return Number.isNaN(date.getTime()) ? dateString : date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };
  const formatTimeOnly = (dateString: string | null | undefined) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return Number.isNaN(date.getTime()) ? dateString : date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };
  const formatDateTimeHeader = (dateString: string) => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
    const timeStr = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true
    });
    return { dateStr, timeStr };
  };
  const getAppointmentDateAndTimeDisplay = (): { dateStr: string; timeStr: string } => {
    const apt = appointment!;
    // Prefer firm scheduledAt (e.g. after reschedule) over a leftover flex window
    if (apt.scheduledAt) {
      return {
        dateStr: formatDateOnly(apt.scheduledAt),
        timeStr: formatTimeOnly(apt.scheduledAt)
      };
    }
    if (apt.flexScheduledAt?.earliest && apt.flexScheduledAt?.latest) {
      return {
        dateStr: formatDateOnly(apt.flexScheduledAt.earliest),
        timeStr: `Between ${formatTimeOnly(apt.flexScheduledAt.earliest)} – ${formatTimeOnly(apt.flexScheduledAt.latest)}`
      };
    }
    const displayDate = apt.flexScheduledAt?.earliest ?? null;
    if (!displayDate) return { dateStr: 'Date TBD', timeStr: '—' };
    return {
      dateStr: formatDateOnly(displayDate),
      timeStr: formatTimeOnly(displayDate)
    };
  };

  const getDaysDifference = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    const diffTime = date.getTime() - today.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';

    if (diffDays < 0) {
      const n = Math.abs(diffDays);
      if (n === 1) return '1 day ago';
      return `${n} days ago`;
    }

    if (diffDays === 1) return 'in 1 day';
    return `in ${diffDays} days`;
  };

  // Handle private notes edit
  const handleSavePrivateNotes = async () => {
    if (!appointment || !currentBusiness?.id) return;
    
    setPrivateNotesSaving(true);
    try {
      await apiService.updateCustomerPrivateNotes(appointment.customerId, currentBusiness.id, privateNotes);
      setIsEditingPrivateNotes(false);
      toast.success('Private notes saved successfully');
    } catch (error: any) {
      console.error('Error saving private notes:', error);
      toast.error(error.response?.data?.message || 'Failed to save private notes');
    } finally {
      setPrivateNotesSaving(false);
    }
  };

  const handleCancelEditPrivateNotes = async () => {
    if (!appointment || !currentBusiness?.id) return;
    try {
      const notesData = await apiService.getCustomerPrivateNotes(appointment.customerId, currentBusiness.id);
      setPrivateNotes(notesData.notes || '');
    } catch (error) {
      console.error('Error loading private notes:', error);
    }
    setIsEditingPrivateNotes(false);
  };

  // Helper function to format time for display
  const formatTimeForDisplay = (time: string): string => {
    if (!time) return '';
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Helper function to format operating hours
  const formatOperatingHours = (hours: any[]): string => {
    if (!hours || hours.length === 0) return 'Hours not set';
    
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayNamesFull = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    // Group consecutive days with same hours
    const sortedHours = [...hours]
      .filter(h => !h.isClosed)
      .sort((a, b) => a.dayOfWeek - b.dayOfWeek);
    
    if (sortedHours.length === 0) return 'Closed';
    
    // Simple format: show first and last day if all days are the same
    if (sortedHours.length === 7) {
      const first = sortedHours[0];
      const openTime = formatTimeForDisplay(first.openTime);
      const closeTime = formatTimeForDisplay(first.closeTime);
      return `Mon–Sun: ${openTime}–${closeTime}`;
    }
    
    // Show range if consecutive days have same hours
    let result = '';
    let startDay = sortedHours[0].dayOfWeek;
    let endDay = sortedHours[0].dayOfWeek;
    let currentOpen = sortedHours[0].openTime;
    let currentClose = sortedHours[0].closeTime;
    
    for (let i = 1; i < sortedHours.length; i++) {
      const hour = sortedHours[i];
      if (hour.openTime === currentOpen && hour.closeTime === currentClose && hour.dayOfWeek === endDay + 1) {
        endDay = hour.dayOfWeek;
      } else {
        // Output current range
        if (result) result += ', ';
        if (startDay === endDay) {
          result += `${dayNames[startDay]}: ${formatTimeForDisplay(currentOpen)}–${formatTimeForDisplay(currentClose)}`;
        } else {
          result += `${dayNames[startDay]}–${dayNames[endDay]}: ${formatTimeForDisplay(currentOpen)}–${formatTimeForDisplay(currentClose)}`;
        }
        // Start new range
        startDay = hour.dayOfWeek;
        endDay = hour.dayOfWeek;
        currentOpen = hour.openTime;
        currentClose = hour.closeTime;
      }
    }
    
    // Output last range
    if (result) result += ', ';
    if (startDay === endDay) {
      result += `${dayNames[startDay]}: ${formatTimeForDisplay(currentOpen)}–${formatTimeForDisplay(currentClose)}`;
    } else {
      result += `${dayNames[startDay]}–${dayNames[endDay]}: ${formatTimeForDisplay(currentOpen)}–${formatTimeForDisplay(currentClose)}`;
    }
    
    return result;
  };

  const serviceIdToLocation = React.useMemo(() => {
    const map = new Map<string, string>();
    (businessServices || []).forEach((s: any) => {
      const id = String(s?.id ?? '');
      if (!id) return;
      const raw = s?.serviceLocation ?? s?.service_location;
      const loc = typeof raw === 'string' ? raw.toUpperCase().trim() : '';
      if (loc) map.set(id, loc);
    });
    return map;
  }, [businessServices]);

  const serviceLocationSummary = React.useMemo(() => {
    const ids = (appointment?.assignments || []).map((a: any) => String(a?.serviceId ?? a?.service_id ?? '')).filter(Boolean);
    const locations = ids.map((id) => serviceIdToLocation.get(id)).filter((x): x is string => typeof x === 'string' && !!x);
    const hasOnSite = locations.some((l) => l === 'ON_SITE' || l === 'ONSITE');
    const hasInStore = locations.some((l) => l === 'IN_STORE' || l === 'INSTORE');

    const label =
      hasOnSite && hasInStore ? 'Mixed (In-store + On-site)'
      : hasOnSite ? 'On-site'
      : hasInStore ? 'In-store'
      : null;

    const legacyAssignmentWithServiceAddress = (appointment?.assignments || []).find(
      (a) => !!(a as any)?.service_address
    ) as any;

    const rawAddr =
      (appointment?.assignments || []).find((a) => a?.serviceAddress)?.serviceAddress ||
      legacyAssignmentWithServiceAddress?.service_address ||
      null;

    const addr = rawAddr && typeof rawAddr === 'object' ? rawAddr : null;
    const hasValidAddress = !!(
      addr &&
      typeof addr.address === 'string' && addr.address.trim() &&
      typeof addr.city === 'string' && addr.city.trim() &&
      typeof addr.state === 'string' && addr.state.trim() &&
      typeof addr.zip === 'string' && addr.zip.trim()
    );

    return {
      hasOnSite,
      label,
      address: hasValidAddress ? addr : null
    };
  }, [appointment?.assignments, serviceIdToLocation]);

  // Close actions dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Don't close if clicking anything inside the dropdown container (button or menu items)
      if (
        (actionsDropdownDesktopRef.current && actionsDropdownDesktopRef.current.contains(target)) ||
        (actionsDropdownMobileRef.current && actionsDropdownMobileRef.current.contains(target))
      ) return;
      setIsActionsDropdownOpen(false);
    };

    if (isActionsDropdownOpen) {
      // Use click event with a small delay to allow button onClick handlers to fire first
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [isActionsDropdownOpen]);

  const [cancelScope, setCancelScope] = useState<'THIS' | 'REMAINING'>('THIS');
  const cancelScopeRef = useRef<'THIS' | 'REMAINING'>('THIS');
  const [seriesSummary, setSeriesSummary] = useState<any | null>(null);
  const [isSeriesLoading, setIsSeriesLoading] = useState(false);
  const [isSeriesListModalOpen, setIsSeriesListModalOpen] = useState(false);
  const [seriesListItems, setSeriesListItems] = useState<AppointmentSeriesListItem[]>([]);
  const [seriesListLoading, setSeriesListLoading] = useState(false);

  const handleOpenSeriesListModal = useCallback(async () => {
    if (!appointment?.id) return;
    setIsSeriesListModalOpen(true);
    setSeriesListLoading(true);
    setSeriesListItems([]);
    try {
      const { appointments } = await apiService.getAppointmentSeriesAppointments(appointment.id);
      setSeriesListItems(appointments);
    } catch (err: any) {
      console.error('Error loading series appointments:', err);
      toast.error(err?.response?.data?.message || 'Could not load series appointments');
    } finally {
      setSeriesListLoading(false);
    }
  }, [appointment?.id]);

  const handleStatusChange = async (
    newStatus: string,
    cancellationNotesText?: string,
    isNoShow?: boolean,
    sendEmail?: boolean,
    cancelScopeArg?: 'THIS' | 'REMAINING'
  ) => {
    if (!appointment) return;
    
    setIsUpdating(true);
    try {
      if (newStatus === 'CANCELLED') {
        // Always use the cancel endpoint when cancelling to properly handle noShow and cancellationNotes
        await apiService.cancelAppointment(appointment.id, {
          noShow: isNoShow || false,
          cancellationNotes: cancellationNotesText ? cancellationNotesText.trim() : '',
          ...(cancelScopeArg ? { cancelScope: cancelScopeArg } : {}),
          ...(sendEmail !== undefined ? { sendCancellationEmail: sendEmail } : {})
        });
        // Reload the appointment to get updated data
        const updatedAppointment = await apiService.getAppointment(appointment.id);
        setAppointment(updatedAppointment);
        refreshActivityEvents();
        toast.success('Appointment cancelled successfully!');
      } else {
        // Use the regular update endpoint for other status changes
        const updatedAppointment = await apiService.updateAppointment(appointment.id, {
          status: newStatus as any
        });
        setAppointment(updatedAppointment);
        refreshActivityEvents();
        toast.success('Appointment status updated successfully!');
      }
    } catch (error: any) {
      console.error('Error updating appointment status:', error);
      toast.error(error.response?.data?.message || 'Failed to update appointment status');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleStatusChangeWithConfirmation = (newStatus: string, actionName: string) => {
    const messages: { [key: string]: { title: string; message: string; confirmText: string; confirmButtonClass: string; showCancellationNotes?: boolean } } = {
      'CONFIRMED': {
        title: 'Confirm Appointment',
        message: 'Are you sure you want to confirm this appointment?',
        confirmText: 'Confirm Appointment',
        confirmButtonClass: 'bg-green-600 hover:bg-green-700'
      },
      'COMPLETED': {
        title: 'Mark as Completed',
        message: 'Are you sure you want to mark this appointment as completed?',
        confirmText: 'Mark as Completed',
        confirmButtonClass: 'bg-blue-600 hover:bg-blue-700'
      },
      'CANCELLED': {
        title: 'Cancel Appointment',
        message: 'Are you sure you want to cancel this appointment?',
        confirmText: 'Cancel Appointment',
        confirmButtonClass: 'bg-red-600 hover:bg-red-700',
        showCancellationNotes: true
      }
    };

    const config = messages[newStatus];
    if (!config) return;

    const depositWarning =
      newStatus === 'CONFIRMED' && appointment && isPendingDepositOutstanding(appointment)
        ? 'This appointment has a Pending Deposit — the deposit has not been paid yet.'
        : undefined;

    // Reset cancellation notes, no show, and send email when opening dialog
    if (config.showCancellationNotes) {
      setCancellationNotes('');
      setNoShow(false);
      setSendCancellationEmail(false);
      setCancelScope('THIS');
      cancelScopeRef.current = 'THIS';
      setSeriesSummary(null);
      cancellationNotesRef.current = '';
      noShowRef.current = false;
      sendCancellationEmailRef.current = false;
    }

    setConfirmationDialog({
      isOpen: true,
      title: config.title,
      message: config.message,
      confirmText: config.confirmText,
      cancelText: 'Go Back',
      confirmButtonClass: config.confirmButtonClass,
      showCancellationNotes: config.showCancellationNotes,
      ...(depositWarning ? { depositWarning } : {}),
      onConfirm: () => {
        // Use refs to get the latest values at execution time
        const currentNotes = cancellationNotesRef.current;
        const currentNoShow = noShowRef.current;
        const currentSendEmail = sendCancellationEmailRef.current;
        const currentCancelScope = cancelScopeRef.current;
        
        // Close dialog and reset state
        setConfirmationDialog(null);
        setCancellationNotes('');
        setNoShow(false);
        setSendCancellationEmail(false);
        cancellationNotesRef.current = '';
        noShowRef.current = false;
        sendCancellationEmailRef.current = false;
        
        // Execute the action with captured values
        if (newStatus === 'CANCELLED') {
          handleStatusChange(newStatus, currentNotes, currentNoShow, currentSendEmail, currentCancelScope);
        } else {
          handleStatusChange(newStatus);
        }
      }
    });
  };

  const handleNoShowCancelClick = () => {
    setConfirmationDialog({
      isOpen: true,
      title: 'Mark as No Show',
      message: 'The appointment is about to be canceled due to no show. Do you want to proceed?',
      confirmText: 'Yes, proceed',
      cancelText: 'Go Back',
      confirmButtonClass: 'bg-red-600 hover:bg-red-700',
      onConfirm: async () => {
        setConfirmationDialog(null);
        try {
          await handleStatusChange('CANCELLED', '', true);
        } catch (error) {
          // Error already handled in handleStatusChange
        }
      }
    });
  };

  const handleMarkDepositPaidClick = () => {
    if (!appointment || !isDepositOutstanding(appointment)) return;
    setIsMarkDepositPaidModalOpen(true);
  };

  const handleMarkDepositPaidSubmit = async (payment: {
    paymentMethod: string;
    paymentDate: string;
    referenceNumber?: string | null;
    notes?: string | null;
  }) => {
    if (!appointment) return;
    try {
      const updatedAppointment = await apiService.markAppointmentDepositPaid(appointment.id, payment);
      setAppointment(updatedAppointment);
      refreshActivityEvents();
      toast.success('Deposit marked as paid successfully!');
    } catch (error: any) {
      console.error('Error marking deposit paid:', error);
      toast.error(error.response?.data?.message || 'Failed to mark deposit as paid');
      throw error;
    }
  };

  const [isMarkDepositPaidModalOpen, setIsMarkDepositPaidModalOpen] = useState(false);
  const [isDepositPaymentDetailsModalOpen, setIsDepositPaymentDetailsModalOpen] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [notesText, setNotesText] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    cancelText: string;
    onConfirm: () => void;
    confirmButtonClass?: string;
    showCancellationNotes?: boolean;
    depositWarning?: string;
  } | null>(null);
  const [cancellationNotes, setCancellationNotes] = useState('');
  const [noShow, setNoShow] = useState(false);
  const [sendCancellationEmail, setSendCancellationEmail] = useState(false);
  const cancellationNotesRef = useRef('');
  const noShowRef = useRef(false);
  const sendCancellationEmailRef = useRef(false);

  // Load series summary when opening the cancel confirmation
  useEffect(() => {
    let cancelled = false;
    const isCancelDialogOpen = Boolean(confirmationDialog?.isOpen) && confirmationDialog?.title === 'Cancel Appointment';
    const appointmentId = appointment?.id;
    if (!isCancelDialogOpen || !appointmentId) return;

    setIsSeriesLoading(true);
    apiService
      .getAppointmentSeriesSummary(String(appointmentId))
      .then((data) => {
        if (cancelled) return;
        setSeriesSummary(data);
      })
      .catch(() => {
        if (cancelled) return;
        setSeriesSummary(null);
      })
      .finally(() => {
        if (cancelled) return;
        setIsSeriesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [confirmationDialog?.isOpen, confirmationDialog?.title, appointment?.id]);

  const handleSendReminder = async () => {
    if (!appointment) return;
    
    setSendingReminder(true);
    try {
      const result = await apiService.sendAppointmentReminder(appointment.id);
      
      // Update the appointment with the new reminder_sent_at timestamp
      setAppointment({
        ...appointment,
        reminderSentAt: result.reminder_sent_at
      });
      refreshActivityEvents();
      toast.success('Reminder email sent successfully!');
    } catch (error: any) {
      console.error('Error sending reminder:', error);
      toast.error(error.response?.data?.message || 'Failed to send reminder email');
    } finally {
      setSendingReminder(false);
    }
  };

  const handleOpenNotesModal = () => {
    if (!appointment) return;
    setNotesText(appointment.notes || '');
    setIsNotesModalOpen(true);
  };

  const handleCloseNotesModal = () => {
    setIsNotesModalOpen(false);
    if (appointment) {
      setNotesText(appointment.notes || '');
    }
  };

  const handleSaveNotes = async () => {
    if (!appointment) return;
    
    setIsSavingNotes(true);
    try {
      const updatedAppointment = await apiService.updateAppointment(appointment.id, {
        notes: notesText.trim()
      });
      
      setAppointment(updatedAppointment);
      setIsNotesModalOpen(false);
      toast.success('Notes saved successfully!');
    } catch (error: any) {
      console.error('Error saving notes:', error);
      toast.error(error.response?.data?.message || 'Failed to save notes');
    } finally {
      setIsSavingNotes(false);
    }
  };

  const handleBack = () => {
    // Get the 'from' parameter from the URL to determine which section to navigate back to
    const urlParams = new URLSearchParams(window.location.search);
    const fromSection = urlParams.get('from');
    
    if (fromSection === 'customers' && appointment?.customerId) {
      // Navigate back to the customer details page
      navigate(`/dashboard/business/customers/${appointment.customerId}`);
    } else if (fromSection) {
      // Navigate back to the section the user came from
      if (shouldShowBusinessDashboard(user!)) {
        navigate(`/dashboard/business?section=${fromSection}`);
      } else {
        navigate(`/dashboard/customer?section=${fromSection}`);
      }
    } else {
      // Fallback logic if no 'from' parameter is provided
      if (user?.canPerformServices) {
        navigate('/dashboard/business?section=my-appointments');
      } else if (user?.roles?.some(role => 
        role.isActive && 
        role.permissions?.some(permission => 
          ['manage_roles', 'manage_professionals', 'manage_settings'].includes(permission)
        )
      )) {
        navigate('/dashboard/business?section=appointments');
      } else {
        navigate('/dashboard/customer?section=appointments');
      }
    }
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Fetch assignment data for professionals
  const fetchAssignmentData = async (professionals: any[], businessId: string, assignment: any) => {
    if (!appointment) return;
    
    setLoadingAssignmentData(true);
    try {
      // Fetch capabilities for all professionals
      const capabilityPromises = professionals.map(async (professional) => {
        try {
          const capabilities = await apiService.getProfessionalServices(businessId, professional.id);
          return { professionalId: professional.id, capabilities: capabilities || [] };
        } catch (error) {
          console.error(`Error fetching capabilities for ${professional.id}:`, error);
          return { professionalId: professional.id, capabilities: [] };
        }
      });

      // Fetch schedules for all professionals
      const schedulePromises = professionals.map(async (professional) => {
        try {
          const schedules = await apiService.getProfessionalAvailability(businessId, professional.id);
          return { professionalId: professional.id, schedules: schedules || [] };
        } catch (error) {
          console.error(`Error fetching schedule for ${professional.id}:`, error);
          return { professionalId: professional.id, schedules: [] };
        }
      });

      // Fetch existing appointments for all professionals for the appointment date
      const displayDateIso = appointment.scheduledAt ?? appointment.flexScheduledAt?.earliest;
      const appointmentDate = displayDateIso ? new Date(displayDateIso) : new Date();
      const year = appointmentDate.getFullYear();
      const month = String(appointmentDate.getMonth() + 1).padStart(2, '0');
      const day = String(appointmentDate.getDate()).padStart(2, '0');
      const appointmentDateStr = `${year}-${month}-${day}T00:00:00`;
      const appointmentDateEndStr = `${year}-${month}-${day}T23:59:59`;
      
      const appointmentPromises = professionals.map(async (professional) => {
        try {
          const result = await apiService.getAppointments({
            businessId,
            professionalId: professional.id,
            startDate: appointmentDateStr,
            endDate: appointmentDateEndStr,
            status: 'upcoming'
          });
          return { professionalId: professional.id, appointments: result.data || [] };
        } catch (error) {
          console.error(`Error fetching appointments for ${professional.id}:`, error);
          return { professionalId: professional.id, appointments: [] };
        }
      });

      const [capabilityResults, scheduleResults, appointmentResults] = await Promise.all([
        Promise.all(capabilityPromises),
        Promise.all(schedulePromises),
        Promise.all(appointmentPromises)
      ]);

      const capabilitiesMap: {[professionalId: string]: any[]} = {};
      capabilityResults.forEach(result => {
        capabilitiesMap[result.professionalId] = result.capabilities;
      });

      const schedulesMap: {[professionalId: string]: any[]} = {};
      scheduleResults.forEach(result => {
        schedulesMap[result.professionalId] = result.schedules;
      });

      const appointmentsMap: {[professionalId: string]: Appointment[]} = {};
      appointmentResults.forEach(result => {
        appointmentsMap[result.professionalId] = result.appointments;
      });

      setAssignmentCapabilities(capabilitiesMap);
      setAssignmentSchedules(schedulesMap);
      setAssignmentExistingAppointments(appointmentsMap);
    } catch (error) {
      console.error('Error fetching assignment data:', error);
    } finally {
      setLoadingAssignmentData(false);
    }
  };

  // Check if professional has capability for a service
  const hasServiceCapability = (professionalId: string, serviceId: string): boolean => {
    const capabilities = assignmentCapabilities[professionalId] || [];
    if (capabilities.length === 0) return false;
    return capabilities.some((capability: any) => {
      const capId = capability.id || capability.serviceId || capability.service_id;
      return String(capId) === String(serviceId);
    });
  };

  // Check if professional has schedule conflict
  const hasScheduleConflict = (professionalId: string, scheduledAt: string, serviceDuration: number): boolean => {
    if (!appointment) return false;
    
    const schedules = assignmentSchedules[professionalId] || [];
    if (schedules.length === 0) return true; // No schedule = conflict
    
    const appointmentDateTime = new Date(scheduledAt);
    const dayOfWeek = appointmentDateTime.getDay(); // 0 = Sunday, 6 = Saturday
    const appointmentTime = appointmentDateTime.toTimeString().slice(0, 5); // HH:mm format
    
    const daySchedule = schedules.find((schedule: any) => {
      const scheduleDay = schedule.dayOfWeek !== undefined ? schedule.dayOfWeek : schedule.day_of_week;
      return scheduleDay === dayOfWeek;
    });
    
    if (!daySchedule || !daySchedule.isAvailable) return true;
    
    const startTime = daySchedule.startTime || daySchedule.start_time || '09:00';
    const endTime = daySchedule.endTime || daySchedule.end_time || '17:00';
    
    // Normalize times for comparison
    const normalizeTime = (time: string) => {
      const [hours, minutes] = time.split(':').map(Number);
      return hours * 60 + minutes;
    };
    
    const normalizedAppointmentTime = normalizeTime(appointmentTime);
    const normalizedStartTime = normalizeTime(startTime);
    const normalizedEndTime = normalizeTime(endTime);
    const appointmentEndTime = normalizedAppointmentTime + serviceDuration;
    
    const isBeforeStart = normalizedAppointmentTime < normalizedStartTime;
    const isAfterEnd = appointmentEndTime > normalizedEndTime;
    
    if (isBeforeStart || isAfterEnd) return true;
    
    // Check for existing appointment conflicts
    const existingAppointments = assignmentExistingAppointments[professionalId] || [];
    const otherAppointments = existingAppointments.filter(apt => apt.id !== appointment.id);
    
    for (const existingApt of otherAppointments) {
      const existingStartIso = existingApt.scheduledAt ?? existingApt.flexScheduledAt?.earliest;
      if (!existingStartIso) continue;
      const existingStart = new Date(existingStartIso);
      const existingDuration = existingApt.assignments?.reduce((total: number, assignment: any) => {
        return total + (assignment.durationMinutes || 0);
      }, 0) || 0;
      const existingEnd = new Date(existingStart.getTime() + existingDuration * 60 * 1000);
      
      const appointmentEnd = new Date(appointmentDateTime.getTime() + serviceDuration * 60 * 1000);
      
      if (
        (appointmentDateTime >= existingStart && appointmentDateTime < existingEnd) ||
        (appointmentEnd > existingStart && appointmentEnd <= existingEnd) ||
        (appointmentDateTime <= existingStart && appointmentEnd >= existingEnd)
      ) {
        return true;
      }
    }
    
    return false;
  };

  // Handle opening assignment modal
  const handleOpenAssignmentModal = async (assignment: any) => {
    if (!currentBusiness?.id || !appointment) return;
    
    setSelectedAssignment(assignment);
    setSelectedProfessionalId(assignment.professionalId || '');
    
    try {
      const profs = await apiService.getBusinessProfessionalsDetailed(currentBusiness.id);
      const formattedProfs = profs.map((prof: any) => ({
        id: prof.id,
        firstName: prof.name?.split(' ')[0] || prof.firstName || '',
        lastName: prof.name?.split(' ').slice(1).join(' ') || prof.lastName || '',
        email: prof.email,
        phone: prof.phone,
        canPerformServices: prof.canPerformServices
      }));
      
      if (formattedProfs && formattedProfs.length > 0) {
        setAssignmentProfessionals(formattedProfs);
        await fetchAssignmentData(formattedProfs, currentBusiness.id, assignment);
        setIsAssignmentModalOpen(true);
      } else {
        toast.error('No professionals available for this business');
      }
    } catch (error) {
      console.error('Error loading professionals for assignment:', error);
      toast.error('Failed to load professionals');
    }
  };

  // Handle saving assignment
  const handleSaveAssignment = async () => {
    if (!appointment || !selectedAssignment || !currentBusiness?.id) return;

    setIsSavingAssignment(true);
    try {
      // Update the specific assignment with selected professional
      const updatedAssignments = appointment.assignments.map((assignment: any) => {
        if (assignment.id === selectedAssignment.id) {
          return {
            id: assignment.id,
            serviceId: assignment.serviceId,
            professionalId: selectedProfessionalId || undefined,
            durationMinutes: assignment.durationMinutes,
            price: assignment.price,
            notes: assignment.notes
          };
        }
        return assignment;
      });

      await apiService.updateAppointment(appointment.id, {
        assignments: updatedAssignments
      } as any);

      toast.success('Professional assignment updated successfully');
      setIsAssignmentModalOpen(false);
      setSelectedAssignment(null);
      setSelectedProfessionalId('');
      
      // Reload appointment to get updated data
      const updatedAppointment = await apiService.getAppointment(appointment.id);
      setAppointment(updatedAppointment);
      refreshActivityEvents();
    } catch (error) {
      console.error('Error saving assignment:', error);
      toast.error('Failed to save assignment');
    } finally {
      setIsSavingAssignment(false);
    }
  };

  // Get navigation items based on user role
  const getActiveSection = (): string => {
    // Get the 'from' parameter from the URL to determine which section the user came from
    const urlParams = new URLSearchParams(window.location.search);
    const fromSection = urlParams.get('from');
    
    // If we have a 'from' parameter, use it directly
    if (fromSection) {
      return fromSection;
    }

    // Fallback logic if no 'from' parameter is provided
    if (!appointment || !user) return 'appointments';

    // If user is the customer of this appointment, highlight "My Bookings" (for business users) or "My Appointments" (for customers)
    if (appointment.customerId === user.id) {
      if (shouldShowBusinessDashboard(user)) {
        return 'my-bookings';
      } else {
        return 'appointments';
      }
    }

    // Check if user is a business owner (has business management permissions)
    const isBusinessOwner = user.roles?.some(role => 
      role.isActive && 
      role.permissions?.some(permission => 
        ['manage_roles', 'manage_professionals', 'manage_settings'].includes(permission)
      )
    );

    // If user is a business owner, they should see "Appointments" highlighted when viewing from business context
    // Only show "My Appointments" if they are NOT a business owner but are a professional for this appointment
    if (isBusinessOwner) {
      return 'appointments';
    }

    // If user is not a business owner but is a professional for this appointment, highlight "My Appointments"
    const isProfessionalForAppointment = appointment.assignments.some(
      assignment => assignment.professionalId === user.id
    );
    if (isProfessionalForAppointment) {
      return 'my-appointments';
    }

    // If user is a professional but not assigned to this appointment, highlight "Appointments"
    if (shouldShowBusinessDashboard(user)) {
      return 'appointments';
    }

    // Default fallback
    return 'appointments';
  };

  // Navigation items are now handled by BusinessDashboardSidebar component

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !appointment) {
    const getErrorIcon = () => {
      switch (errorType) {
        case 'not_found': return '🔍';
        case 'unauthorized': return '🔒';
        case 'server_error': return '⚠️';
        default: return '⚠️';
      }
    };

    const getErrorTitle = () => {
      switch (errorType) {
        case 'not_found': return 'Appointment Not Found';
        case 'unauthorized': return 'Access Denied';
        case 'server_error': return 'Error Loading Appointment';
        default: return 'Error Loading Appointment';
      }
    };

    const getErrorAction = () => {
      switch (errorType) {
        case 'unauthorized':
          return (
            <button
              onClick={() => navigate('/login')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Login
            </button>
          );
        default:
          return (
            <button
              onClick={handleBack}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Appointments
            </button>
          );
      }
    };

    return (
      <div className="min-h-screen bg-gray-50">
        <NavigationBar 
          unreadNotificationsCount={unreadNotifications.length}
          onNotificationsClick={handleNotificationsClick}
        />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">{getErrorIcon()}</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{getErrorTitle()}</h2>
            <p className="text-gray-600 mb-4">{error || 'Appointment not found'}</p>
            {getErrorAction()}
          </div>
        </div>
      </div>
    );
  }

  const displayTotalPrice =
    appointment.quoteFinalAgreedAmount != null && !Number.isNaN(Number(appointment.quoteFinalAgreedAmount))
      ? Number(appointment.quoteFinalAgreedAmount)
      : getTotalPrice(appointment);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <NavigationBar 
        unreadNotificationsCount={unreadNotifications.length}
        onNotificationsClick={handleNotificationsClick}
      />
      
      <div className="flex-1 lg:grid lg:grid-cols-[auto_1fr] lg:min-h-screen">
        {shouldShowBusinessDashboard(user!) && (
          <BusinessDashboardSidebar
            isSidebarOpen={isSidebarOpen}
            isSidebarCollapsed={isSidebarCollapsed}
            activeSection={getActiveSection()}
            onClose={() => setIsSidebarOpen(false)}
            onToggleCollapse={toggleSidebar}
            unreadNotificationCount={unreadNotifications.length}
          />
        )}
        
        {/* Main Content */}
        <div className="flex flex-col min-h-screen">
          {/* Mobile Header */}
          <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="text-lg font-semibold text-gray-900">Appointment Details</h1>
              {shouldShowBusinessDashboard(user!) && appointment?.status !== 'CANCELLED' && appointment?.status !== 'COMPLETED' ? (
                <div className="relative" ref={actionsDropdownMobileRef}>
                  <button
                    onClick={() => setIsActionsDropdownOpen(!isActionsDropdownOpen)}
                    className="p-2 rounded-md hover:bg-gray-100 flex items-center justify-center"
                    title="Actions"
                    aria-label="Actions"
                  >
                    <Settings className="w-8 h-8 text-gray-700 -m-1" />
                  </button>
                  {isActionsDropdownOpen && (
                    <div className="absolute right-0 top-full mt-2 min-w-[240px] bg-white border border-gray-300 rounded-lg shadow-lg z-30 py-2">
                      {appointment?.status === 'PENDING' && (
                        <button
                          onClick={() => {
                            setIsActionsDropdownOpen(false);
                            handleStatusChangeWithConfirmation('CONFIRMED', 'Confirm Appointment');
                          }}
                          className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                        >
                          <i className="bi bi-check-circle text-green-600"></i>
                          Confirm Appointment
                        </button>
                      )}
                      {isDepositOutstanding(appointment) && (
                        <button
                          onClick={() => {
                            setIsActionsDropdownOpen(false);
                            handleMarkDepositPaidClick();
                          }}
                          className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                        >
                          <i className="bi bi-cash-coin text-green-600"></i>
                          Mark Deposit Paid
                        </button>
                      )}
                      {appointment?.status === 'CONFIRMED' && (
                        <button
                          onClick={() => {
                            setIsActionsDropdownOpen(false);
                            handleStatusChangeWithConfirmation('COMPLETED', 'Mark as Completed');
                          }}
                          className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                        >
                          <i className="bi bi-check2-square text-green-600"></i>
                          Mark as Completed
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setIsActionsDropdownOpen(false);
                          navigate(`/dashboard/business/reschedule/${appointment?.id}`);
                        }}
                        className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                      >
                        <i className="bi bi-calendar-event text-blue-600"></i>
                        Reschedule Appointment
                      </button>
                      <button
                        onClick={() => {
                          setIsActionsDropdownOpen(false);
                          handleNoShowCancelClick();
                        }}
                        className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                      >
                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                        Mark as No Show
                      </button>
                      <button
                        onClick={() => {
                          setIsActionsDropdownOpen(false);
                          handleStatusChangeWithConfirmation('CANCELLED', 'Cancel Appointment');
                        }}
                        className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                      >
                        <i className="bi bi-x-octagon text-red-600"></i>
                        Cancel Appointment
                      </button>
                      <button
                        onClick={() => {
                          setIsActionsDropdownOpen(false);
                          navigate(`/dashboard/business/appointments/${appointment?.id}/edit${window.location.search || ''}`);
                        }}
                        className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                      >
                        <i className="bi bi-pencil-square text-gray-700"></i>
                        Edit Appointment Details
                      </button>
                      <div className="h-px bg-gray-200 my-2"></div>
                      <button
                        onClick={() => {
                          setIsActionsDropdownOpen(false);
                          handleSendReminder();
                        }}
                        disabled={sendingReminder || appointment?.status === 'PENDING'}
                        className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <i className="bi bi-bell text-amber-600"></i>
                        Send Reminder
                      </button>
                      <div className="h-px bg-gray-200 my-2"></div>
                      <button
                        onClick={() => {
                          setIsActionsDropdownOpen(false);
                          if (!appointment?.id) return;
                          if (appointment.invoiceId) {
                            navigate(`/dashboard/business/invoices/${appointment.invoiceId}`, {
                              state: { from: `${routerLocation.pathname}${routerLocation.search || ''}` },
                            });
                          } else {
                            navigate(`/dashboard/business/appointments/${appointment.id}/create-invoice`, {
                              state: { from: `${routerLocation.pathname}${routerLocation.search || ''}` },
                            });
                          }
                        }}
                        disabled={!appointment?.id}
                        className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <i className="bi bi-receipt text-blue-600"></i>
                        {appointment?.invoiceId ? 'View Invoice' : 'Create Invoice'}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-10"></div> /* Spacer for centering */
              )}
            </div>
          </div>
          
          <div className="flex-1 p-4 md:p-8">
            {/* Back Link */}
            <button
              onClick={handleBack}
              className="text-gray-600 hover:text-gray-900 mb-4 flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

              {/* Header with Date/Time and Actions */}
              <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6 gap-4">
                <div className="flex-1">
                  {appointment && (() => {
                    const displayDate = appointment.scheduledAt ?? appointment.flexScheduledAt?.earliest ?? null;
                    const { dateStr, timeStr } = getAppointmentDateAndTimeDisplay();
                    const whenText = displayDate ? getDaysDifference(displayDate) : '—';
                    return (
                      <>
                        {/* Mobile: Date header and status badge on same line */}
                        <div className="flex items-center justify-between gap-4 mb-2 md:block">
                          <h1 className="text-2xl font-semibold text-gray-900">{dateStr}</h1>
                          {/* Status badge - visible in mobile mode, aligned to right */}
                          <span className={`md:hidden inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border flex-shrink-0 ${appointmentDetailsStatusBadgeClass(appointment?.status || '', appointment)}`}>
                            {formatAppointmentStatusLabel(appointment?.status || '', appointment)}
                          </span>
                        </div>
                        <div className="text-gray-600 text-sm flex items-center gap-4">
                          <span className="flex items-center gap-1">
                            <i className="bi bi-clock"></i>
                            {timeStr}
                          </span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <i className="bi bi-hourglass-split"></i>
                            {getTotalDuration(appointment)} min
                          </span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <i className="bi bi-stopwatch"></i>
                            {whenText}
                          </span>
                        </div>
                        {(appointment?.seriesId || (appointment as any)?.series_id) && (
                          <div className="mt-3">
                            <button
                              type="button"
                              onClick={() => void handleOpenSeriesListModal()}
                              aria-label="View all appointments in this series"
                              className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-[#0B4778]/25 bg-[#0B4778]/5 px-3 py-2 text-left text-sm text-[#0B4778] hover:bg-[#0B4778]/10 transition-colors w-full sm:w-auto"
                            >
                              <Repeat2 className="w-4 h-4 flex-shrink-0" aria-hidden />
                              <span className="font-semibold">Recurring series</span>
                              {appointment.occurrenceIndex != null &&
                              appointment.seriesOccurrenceCount != null &&
                              Number(appointment.seriesOccurrenceCount) > 0 ? (
                                <span className="text-gray-700 font-normal">
                                  · Visit {appointment.occurrenceIndex} of {appointment.seriesOccurrenceCount}
                                </span>
                              ) : null}
                            </button>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Actions Dropdown - Desktop: in header, Mobile: hidden here */}
                {appointment?.status !== 'CANCELLED' && appointment?.status !== 'COMPLETED' && (
                  <div className="relative hidden md:block" ref={actionsDropdownDesktopRef}>
                    <button
                      onClick={() => setIsActionsDropdownOpen(!isActionsDropdownOpen)}
                      className="aacore-btn2-primary"
                    >
                      Actions
                      <i className={`bi bi-chevron-down transition-transform duration-200 ${isActionsDropdownOpen ? 'rotate-180' : ''}`}></i>
                    </button>
                    {isActionsDropdownOpen && (
                    <div className="absolute right-0 top-full mt-2 min-w-[240px] bg-white border border-gray-300 rounded-lg shadow-lg z-30 py-2">
                      {appointment?.status === 'PENDING' && (
                        <button
                          onClick={() => {
                            setIsActionsDropdownOpen(false);
                            handleStatusChangeWithConfirmation('CONFIRMED', 'Confirm Appointment');
                          }}
                          className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                        >
                          <i className="bi bi-check-circle text-green-600"></i>
                          Confirm Appointment
                        </button>
                      )}
                      {isDepositOutstanding(appointment) && (
                        <button
                          onClick={() => {
                            setIsActionsDropdownOpen(false);
                            handleMarkDepositPaidClick();
                          }}
                          className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                        >
                          <i className="bi bi-cash-coin text-green-600"></i>
                          Mark Deposit Paid
                        </button>
                      )}
                      {appointment?.status === 'CONFIRMED' && (
                        <button
                          onClick={() => {
                            setIsActionsDropdownOpen(false);
                            handleStatusChangeWithConfirmation('COMPLETED', 'Mark as Completed');
                          }}
                          className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                        >
                          <i className="bi bi-check2-square text-green-600"></i>
                          Mark as Completed
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setIsActionsDropdownOpen(false);
                          navigate(`/dashboard/business/reschedule/${appointment?.id}`);
                        }}
                        className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                      >
                        <i className="bi bi-calendar-event text-blue-600"></i>
                        Reschedule Appointment
                      </button>
                      <button
                        onClick={() => {
                          setIsActionsDropdownOpen(false);
                          handleNoShowCancelClick();
                        }}
                        className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                      >
                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                        Mark as No Show
                      </button>
                      <button
                        onClick={() => {
                          setIsActionsDropdownOpen(false);
                          handleStatusChangeWithConfirmation('CANCELLED', 'Cancel Appointment');
                        }}
                        className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                      >
                        <i className="bi bi-x-octagon text-red-600"></i>
                        Cancel Appointment
                      </button>
                      <button
                        onClick={() => {
                          setIsActionsDropdownOpen(false);
                          navigate(`/dashboard/business/appointments/${appointment?.id}/edit${window.location.search || ''}`);
                        }}
                        className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                      >
                        <i className="bi bi-pencil-square text-gray-700"></i>
                        Edit Appointment Details
                      </button>
                      <div className="h-px bg-gray-200 my-2"></div>
                      <button
                        onClick={() => {
                          setIsActionsDropdownOpen(false);
                          handleSendReminder();
                        }}
                        disabled={sendingReminder || appointment?.status === 'PENDING'}
                        className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <i className="bi bi-bell text-amber-600"></i>
                        Send Reminder
                      </button>
                      <div className="h-px bg-gray-200 my-2"></div>
                      <button
                        onClick={() => {
                          setIsActionsDropdownOpen(false);
                          if (!appointment?.id) return;
                          if (appointment.invoiceId) {
                            navigate(`/dashboard/business/invoices/${appointment.invoiceId}`, {
                              state: { from: `${routerLocation.pathname}${routerLocation.search || ''}` },
                            });
                          } else {
                            navigate(`/dashboard/business/appointments/${appointment.id}/create-invoice`, {
                              state: { from: `${routerLocation.pathname}${routerLocation.search || ''}` },
                            });
                          }
                        }}
                        disabled={!appointment?.id}
                        className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <i className="bi bi-receipt text-blue-600"></i>
                        {appointment?.invoiceId ? 'View Invoice' : 'Create Invoice'}
                      </button>
                    </div>
                  )}
                </div>
              )}
              </div>

              {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6">
                {/* Main Content */}
                <div className="space-y-4">
                  {/* Customer Panel */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Customer</h3>
                    <div className="bg-white rounded-lg border border-gray-200 p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-semibold">
                            {appointment?.customer_first_name?.[0]}{appointment?.customer_last_name?.[0]}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">
                            {appointment?.customer_first_name} {appointment?.customer_last_name}
                          </div>
                          {appointment?.customer_email && !appointment.customer_email.toLowerCase().startsWith('temp-') && (
                            <div className="text-gray-600 text-sm flex items-center gap-2 mt-1">
                              <i className="bi bi-envelope"></i>
                              <span>{appointment.customer_email}</span>
                            </div>
                          )}
                          {appointment?.customer_phone && (
                            <div className="text-gray-600 text-sm flex items-center gap-2 mt-1">
                              <i className="bi bi-telephone"></i>
                              <span>{displayCustomerPhone(appointment.customer_phone, canViewFullCustomerPhone(user, currentBusiness?.id || appointment.businessId))}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Service Location (only when at least one service is ON_SITE) */}
                  {serviceLocationSummary.hasOnSite && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-emerald-600" />
                        Service Location
                      </h3>
                      <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-2 text-sm text-gray-700">
                        <div>
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Location</span>
                          <div className="mt-1 font-medium text-gray-900">{serviceLocationSummary.label || 'On-site'}</div>
                        </div>
                        {serviceLocationSummary.address && (
                          <div className="pt-2 border-t border-gray-200">
                            <div className="font-medium text-gray-900">{serviceLocationSummary.address.address}</div>
                            <div>
                              {serviceLocationSummary.address.city}, {serviceLocationSummary.address.state} {serviceLocationSummary.address.zip}
                            </div>
                            {serviceLocationSummary.address.locationNotes && String(serviceLocationSummary.address.locationNotes).trim() !== '' && (
                              <div className="mt-2 text-gray-600">
                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Location Notes</span>
                                <div className="mt-1">{serviceLocationSummary.address.locationNotes}</div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Services & Charges Panel */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Services & Charges</h3>

                    {/* Service Items */}
                    <div className="space-y-2 mb-4">
                      {appointment?.assignments?.map((assignment, idx) => {
                        const price = typeof assignment.price === 'number' ? assignment.price : parseFloat(assignment.price || '0') || 0;
                        const duration = typeof assignment.durationMinutes === 'number' ? assignment.durationMinutes : 0;
                        const isQuotable = (assignment as any)?.serviceQuotable === true;
                        const showQuotablePriceIcon = isQuotable && (price === 0 || (assignment as any).price == null || (assignment as any).price === '');
                        const hasProfessional = assignment.professionalFirstName && assignment.professionalLastName;
                        const professionalName = hasProfessional && assignment.professionalLastName
                          ? `${assignment.professionalFirstName} ${assignment.professionalLastName.charAt(0)}.`
                          : null;
                        const showProfessionalName = businessProfessionalsCount > 1 && (businessSettings?.showProfessionalNameOnAssignment ?? false) && professionalName;
                        const isUnassigned = !hasProfessional;
                        const canAssign = isUnassigned && (appointment?.status === 'PENDING' || appointment?.status === 'CONFIRMED');
                        const canReassign = hasProfessional && (appointment?.status === 'PENDING' || appointment?.status === 'CONFIRMED');
                        
                        const assignmentIcon = (assignment as any).serviceIcon;
                        const hasCustomIcon = assignmentIcon && typeof assignmentIcon === 'string' && assignmentIcon.trim() !== '';
                        
                        return (
                          <div key={idx} className="grid grid-cols-1 md:grid-cols-[1fr_80px_72px] gap-2 items-center p-3 border border-gray-200 rounded-lg bg-white shadow-sm">
                            <div className="flex items-center gap-2 min-w-0">
                              {hasCustomIcon ? (
                                <span className="text-xl text-green-600 flex-shrink-0">
                                  {assignmentIcon}
                                </span>
                              ) : (
                                <i 
                                  className="bi bi-star-fill flex-shrink-0"
                                  style={{ 
                                    fontSize: '20px',
                                    color: '#FBBF24'
                                  }}
                                ></i>
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="font-semibold text-gray-900 truncate">{assignment.serviceName || 'Unknown Service'}</div>
                                {(() => {
                                  const id = String((assignment as any).serviceId ?? (assignment as any).service_id ?? '');
                                  const rawLoc = id ? serviceIdToLocation.get(id) : '';
                                  const loc = typeof rawLoc === 'string' ? rawLoc.toUpperCase().trim() : '';
                                  const isOnSite = loc === 'ON_SITE' || loc === 'ONSITE';
                                  if (!isOnSite) return null;
                                  return (
                                    <div className="mt-1">
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                                        On-Site
                                      </span>
                                    </div>
                                  );
                                })()}
                                <div className="text-xs text-gray-500 flex items-center gap-2 mt-1">
                                  {showProfessionalName && <i className="bi bi-person-badge"></i>}
                                  {canAssign ? (
                                    <button
                                      onClick={() => handleOpenAssignmentModal(assignment)}
                                      className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                    >
                                      Assign Professional
                                    </button>
                                  ) : canReassign ? (
                                    <span className="flex items-center gap-2">
                                      {showProfessionalName && <span>{professionalName}</span>}
                                      {assignment.insistOnProfessional && showProfessionalName && (
                                        <span className="inline-flex items-center gap-0.5 text-red-500" title="Customer insists on this professional">
                                          <Heart className="w-3 h-3 fill-current" />
                                          <span className="text-xs">Requested</span>
                                        </span>
                                      )}
                                      <button
                                        onClick={() => handleOpenAssignmentModal(assignment)}
                                        className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                      >
                                        Reassign
                                      </button>
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-2">
                                      {showProfessionalName ? professionalName : (hasProfessional ? null : 'Unassigned')}
                                      {assignment.insistOnProfessional && showProfessionalName && professionalName && (
                                        <span className="inline-flex items-center gap-0.5 text-red-500" title="Customer insists on this professional">
                                          <Heart className="w-3 h-3 fill-current" />
                                          <span className="text-xs">Required</span>
                                        </span>
                                      )}
                                    </span>
                                  )}
                                </div>
                                {/* Mobile: Duration and price on same line, left-aligned */}
                                <div className="flex items-center gap-3 mt-2 md:hidden text-sm text-gray-700">
                                  <button
                                    type="button"
                                    onClick={() => openDurationModalForAssignment(assignment)}
                                    disabled={
                                      appointment?.status === 'CANCELLED' ||
                                      appointment?.status === 'COMPLETED' ||
                                      isUpdating
                                    }
                                    className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full border text-xs font-semibold transition-colors whitespace-nowrap ${
                                      appointment?.status !== 'CANCELLED' && appointment?.status !== 'COMPLETED' && !isUpdating
                                        ? 'border-[#0B4778]/20 bg-[#0B4778]/5 text-[#0B4778] hover:bg-[#0B4778]/10'
                                        : 'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed'
                                    }`}
                                    title="Edit duration"
                                  >
                                    <CalendarClock className="w-3.5 h-3.5" />
                                    <span className="whitespace-nowrap">{duration} min</span>
                                  </button>
                                  {showQuotablePriceIcon ? (
                                    <span
                                      className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-700"
                                      title="Quotable price"
                                    >
                                      <HelpCircle className="w-4 h-4" />
                                    </span>
                                  ) : (
                                    <span className="font-mono">${price.toFixed(2)}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            {/* Desktop: Duration and price in separate columns, right-aligned */}
                            <div className="hidden md:block text-right">
                              <button
                                type="button"
                                onClick={() => openDurationModalForAssignment(assignment)}
                                disabled={
                                  appointment?.status === 'CANCELLED' ||
                                  appointment?.status === 'COMPLETED' ||
                                  isUpdating
                                }
                                className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full border text-xs font-semibold transition-colors whitespace-nowrap ${
                                  appointment?.status !== 'CANCELLED' && appointment?.status !== 'COMPLETED' && !isUpdating
                                    ? 'border-[#0B4778]/20 bg-[#0B4778]/5 text-[#0B4778] hover:bg-[#0B4778]/10'
                                    : 'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed'
                                }`}
                                title="Edit duration"
                              >
                                <CalendarClock className="w-3.5 h-3.5" />
                                <span className="whitespace-nowrap">{duration} min</span>
                              </button>
                            </div>
                            <div className="hidden md:block text-right text-sm text-gray-700">
                              {showQuotablePriceIcon ? (
                                <span
                                  className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-700"
                                  title="Quotable price"
                                >
                                  <HelpCircle className="w-4 h-4" />
                                </span>
                              ) : (
                                <span className="font-mono">${price.toFixed(2)}</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Totals */}
                    <div className="flex flex-col items-end gap-1 max-w-[420px] ml-auto">
                      <div className="flex justify-between items-center w-full text-sm">
                        <span className="text-gray-600 text-xs">Subtotal</span>
                        <strong className="font-mono">${displayTotalPrice.toFixed(2)}</strong>
                      </div>
                      <div className="flex justify-between items-center w-full text-lg pt-2 border-t border-gray-200">
                        <strong>Total</strong>
                        <strong className="font-mono">${displayTotalPrice.toFixed(2)}</strong>
                      </div>
                      {appointment?.receivedDepositAmount != null && Number(appointment.receivedDepositAmount) > 0 && (
                        <>
                          <div className="flex justify-between items-center w-full text-sm pt-2">
                            <button
                              type="button"
                              onClick={() => setIsDepositPaymentDetailsModalOpen(true)}
                              className="font-bold underline text-[#0B4778] hover:opacity-80 text-xs text-left"
                            >
                              Deposit paid
                            </button>
                            <strong className="font-mono">${Number(appointment.receivedDepositAmount).toFixed(2)}</strong>
                          </div>
                          {appointment.depositPaidAt && (
                            <div className="flex justify-end w-full text-xs text-gray-500">
                              {formatDate(appointment.depositPaidAt)} at {formatTime(appointment.depositPaidAt)}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Customer Booking Note */}
                  {appointment?.notes && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
                      <div className="flex items-center gap-2 font-semibold text-gray-900 mb-1">
                        <i className="bi bi-chat-left-text text-amber-600"></i>
                        Customer Booking Note
                      </div>
                      <div className="text-gray-600 text-sm mb-2">Provided during booking; visible to staff.</div>
                      <div className="text-gray-900">{renderCustomerBookingNoteContent(appointment.notes)}</div>
                    </div>
                  )}

                  {/* Private Professional Notes */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
                    <div className="flex items-center gap-2 font-semibold text-gray-900 mb-1">
                      <i className="bi bi-lock text-blue-600"></i>
                      Private Professional Notes
                    </div>
                    <div className="text-gray-600 text-sm mb-2">Only visible to you and authorized staff.</div>
                    {isEditingPrivateNotes ? (
                      <div className="space-y-3">
                        <textarea
                          value={privateNotes}
                          onChange={(e) => setPrivateNotes(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                          rows={4}
                          disabled={privateNotesSaving}
                        />
                        <div className="flex items-center gap-2">
                          <button
                            onClick={handleSavePrivateNotes}
                            disabled={privateNotesSaving}
                            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
                          >
                            <i className="bi bi-check"></i>
                            {privateNotesSaving ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={handleCancelEditPrivateNotes}
                            disabled={privateNotesSaving}
                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5"
                          >
                            <i className="bi bi-x"></i>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="text-gray-900 mb-3">{privateNotes || 'No notes yet.'}</div>
                        <button
                          onClick={() => setIsEditingPrivateNotes(true)}
                          className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          <i className="bi bi-pencil"></i>
                          Edit
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Appointment Summary Sidebar */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  {/* Collapsible header for mobile */}
                  <button
                    onClick={() => setIsAppointmentSummaryCollapsed(!isAppointmentSummaryCollapsed)}
                    className="w-full flex items-center justify-between mb-4 md:mb-4 md:pointer-events-none md:cursor-default"
                  >
                    <h3 className="text-lg font-semibold text-gray-900">Appointment Summary</h3>
                    <i className={`bi bi-chevron-down transition-transform duration-200 md:hidden text-gray-500 ${
                      isAppointmentSummaryCollapsed ? '' : 'rotate-180'
                    }`}></i>
                  </button>
                  
                  {/* Content - hidden in mobile when collapsed */}
                  <div className={`md:block ${isAppointmentSummaryCollapsed ? 'hidden' : 'block'}`}>
                    {/* Key-Value Pairs */}
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="bi bi-list-ul text-teal-600"></i>
                        <span>Items</span>
                      </div>
                      <div className="font-semibold text-sm">{appointment?.assignments?.length || 0} {appointment?.assignments?.length === 1 ? 'item' : 'items'}</div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="bi bi-hourglass-split text-purple-600"></i>
                        <span>Total Duration</span>
                      </div>
                      <div className="font-semibold text-sm">{getTotalDuration(appointment!)} min</div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="bi bi-cash-coin text-green-600"></i>
                        <span>Total Price</span>
                      </div>
                      <div className="font-semibold text-sm font-mono">${displayTotalPrice.toFixed(2)}</div>
                    </div>
                    {appointment?.receivedDepositAmount != null && Number(appointment.receivedDepositAmount) > 0 && (
                      <>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-sm text-gray-700">
                            <i className="bi bi-wallet2 text-green-600"></i>
                            <button
                              type="button"
                              onClick={() => setIsDepositPaymentDetailsModalOpen(true)}
                              className="font-bold underline text-[#0B4778] hover:opacity-80 text-left"
                            >
                              Deposit paid
                            </button>
                          </div>
                          <div className="font-semibold text-sm font-mono">${Number(appointment.receivedDepositAmount).toFixed(2)}</div>
                        </div>
                        {appointment.depositPaidAt && (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm text-gray-700">
                              <i className="bi bi-calendar-check text-green-600"></i>
                              <span>Deposit paid on</span>
                            </div>
                            <div className="font-semibold text-sm text-gray-600">{formatDate(appointment.depositPaidAt)} at {formatTime(appointment.depositPaidAt)}</div>
                          </div>
                        )}
                      </>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="bi bi-info-circle text-blue-600"></i>
                        <span>Status</span>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${appointmentDetailsStatusBadgeClass(appointment?.status || '', appointment)}`}>
                        {formatAppointmentStatusLabel(appointment?.status || '', appointment)}
                      </span>
                    </div>
                  </div>

                  <div className="h-px bg-gray-200 my-4"></div>

                  {/* Created/Updated */}
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="bi bi-calendar-plus text-amber-600"></i>
                        <span>Created</span>
                      </div>
                      <div className="font-semibold text-sm">{appointment && formatDateTimeHeader(appointment.createdAt).dateStr}</div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="bi bi-arrow-repeat text-amber-600"></i>
                        <span>Last Updated</span>
                      </div>
                      <div className="font-semibold text-sm">{appointment && formatDateTimeHeader(appointment.updatedAt).dateStr}</div>
                    </div>
                  </div>

                  <div className="h-px bg-gray-200 my-4"></div>

                  {/* Activity Timeline (from appointment_events) */}
                  <AppointmentActivitySection appointmentId={appointmentId ?? undefined} refreshTrigger={activityRefreshTrigger} />

                  <div className="h-px bg-gray-200 my-4"></div>

                  {/* Business Information */}
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Business Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="bi bi-building"></i>
                        <span>{businessInfo?.name || appointment?.business_name || 'Business'}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="bi bi-geo-alt"></i>
                        <span>{businessInfo?.address || 'Address not set'}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="bi bi-telephone"></i>
                        <span>{businessInfo?.phone || 'Phone not set'}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="bi bi-envelope"></i>
                        <span>{businessInfo?.email || 'Email not set'}</span>
                      </div>
                    </div>
                    {operatingHours.length > 0 && (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <i className="bi bi-clock-history"></i>
                          <span>{formatOperatingHours(operatingHours)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  </div>
                </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {confirmationDialog && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[2000] flex items-start sm:items-center justify-center p-4 pt-20 sm:pt-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full border border-gray-200 overflow-hidden max-h-[calc(100vh-6rem)] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 shrink-0 bg-gray-50">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                {confirmationDialog.title === 'Confirm Appointment' && (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                )}
                {confirmationDialog.title === 'Cancel Appointment' && (
                  <Trash2 className="w-5 h-5 text-red-600" />
                )}
                {confirmationDialog.title === 'Mark as No Show' && (
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                )}
                <span>{confirmationDialog.title}</span>
              </h2>
              <button
                onClick={() => {
                  setConfirmationDialog(null);
                  setCancellationNotes('');
                  setNoShow(false);
                  setSendCancellationEmail(false);
                  setCancelScope('THIS');
                  cancelScopeRef.current = 'THIS';
                  setSeriesSummary(null);
                  cancellationNotesRef.current = '';
                  noShowRef.current = false;
                  sendCancellationEmailRef.current = false;
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={isUpdating}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 flex-1 min-h-0 overflow-y-auto">
              <p className="text-gray-700 mb-4">
                {confirmationDialog.message}
              </p>
              {confirmationDialog.depositWarning ? (
                <div
                  role="alert"
                  className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 flex gap-3 items-start"
                >
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" aria-hidden />
                  <span>{confirmationDialog.depositWarning}</span>
                </div>
              ) : null}
              {confirmationDialog.showCancellationNotes && (
                <div className="mt-4 space-y-4">
                  {(() => {
                    const ap: any = appointment as any;
                    const hasSeriesId = Boolean(ap?.seriesId || ap?.series_id);
                    const isRecurring = (seriesSummary as any)?.isRecurring === true || hasSeriesId;
                    if (!isRecurring) return null;

                    const occurrenceIndex =
                      (typeof (seriesSummary as any)?.occurrenceIndex === 'number' ? (seriesSummary as any).occurrenceIndex : null) ??
                      (ap?.occurrenceIndex ?? ap?.occurrence_index ?? null);
                    const seriesCount =
                      (typeof (seriesSummary as any)?.seriesOccurrenceCount === 'number' ? (seriesSummary as any).seriesOccurrenceCount : null) ??
                      (ap?.seriesOccurrenceCount ?? ap?.series_occurrence_count ?? null);

                    const remainingAfterThis =
                      typeof (seriesSummary as any)?.remainingActiveAfterThis === 'number'
                        ? (seriesSummary as any).remainingActiveAfterThis
                        : 0;
                    const showScopeChoice = Number.isFinite(remainingAfterThis) && remainingAfterThis > 0;

                    const completedLabel =
                      typeof (seriesSummary as any)?.completedCount === 'number' ? (seriesSummary as any).completedCount : null;
                    const remainingLabel =
                      typeof (seriesSummary as any)?.remainingActiveIncludingThis === 'number'
                        ? (seriesSummary as any).remainingActiveIncludingThis
                        : null;

                    return (
                      <div className="border border-blue-100 bg-blue-50/40 rounded-md p-4">
                        <p className="text-sm font-semibold text-gray-900">This appointment is part of a series</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {occurrenceIndex && seriesCount ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-blue-200 bg-white text-xs font-semibold text-blue-900">
                              <ListOrdered className="w-3.5 h-3.5 text-blue-700" aria-hidden />
                              <span>
                                {Number(occurrenceIndex)} of {Number(seriesCount)}
                              </span>
                            </span>
                          ) : null}
                          {completedLabel != null ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full border border-gray-200 bg-white text-xs font-semibold text-gray-700">
                              <span>Completed: {completedLabel}</span>
                            </span>
                          ) : null}
                          {remainingLabel != null ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full border border-gray-200 bg-white text-xs font-semibold text-gray-700">
                              <span>Remaining: {remainingLabel}</span>
                            </span>
                          ) : null}
                        </div>

                        {isSeriesLoading ? (
                          <p className="mt-2 text-xs text-gray-600">Checking remaining appointments in this series…</p>
                        ) : null}

                        {showScopeChoice ? (
                          <div className="mt-3">
                            <p className="text-xs font-medium text-gray-700 mb-2">What would you like to cancel?</p>
                            <div className="space-y-2">
                              <label className="flex items-start gap-3 p-3 rounded-md border border-gray-200 bg-white hover:bg-gray-50 cursor-pointer">
                                <input
                                  type="radio"
                                  name="cancelScopeBusinessPage"
                                  value="THIS"
                                  checked={cancelScope === 'THIS'}
                                  onChange={() => {
                                    setCancelScope('THIS');
                                    cancelScopeRef.current = 'THIS';
                                  }}
                                  disabled={isUpdating}
                                  className="mt-1"
                                />
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">Cancel only this appointment</p>
                                  <p className="text-xs text-gray-600">All other upcoming appointments in the series will stay scheduled.</p>
                                </div>
                              </label>
                              <label className="flex items-start gap-3 p-3 rounded-md border border-red-200 bg-white hover:bg-red-50 cursor-pointer">
                                <input
                                  type="radio"
                                  name="cancelScopeBusinessPage"
                                  value="REMAINING"
                                  checked={cancelScope === 'REMAINING'}
                                  onChange={() => {
                                    setCancelScope('REMAINING');
                                    cancelScopeRef.current = 'REMAINING';
                                  }}
                                  disabled={isUpdating}
                                  className="mt-1"
                                />
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">Cancel this and all remaining appointments</p>
                                  <p className="text-xs text-gray-600">
                                    This will also cancel {remainingAfterThis} upcoming appointment{remainingAfterThis === 1 ? '' : 's'} in this series.
                                  </p>
                                </div>
                              </label>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })()}
                  {/* No Show Checkbox */}
                  <div>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={noShow}
                        onChange={(e) => {
                          const value = e.target.checked;
                          setNoShow(value);
                          noShowRef.current = value;
                        }}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                        disabled={isUpdating}
                      />
                      <span className="text-sm font-medium text-gray-900">No Show</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1 ml-6">
                      Check this if the customer didn't show up for the appointment
                    </p>
                  </div>
                  
                  {/* Cancellation Reason */}
                  <div>
                    <label htmlFor="cancellation-notes" className="block text-sm font-medium text-gray-700 mb-2">
                      Cancellation Reason (Optional)
                    </label>
                    <textarea
                      id="cancellation-notes"
                      value={cancellationNotes}
                      onChange={(e) => {
                        const value = e.target.value;
                        setCancellationNotes(value);
                        cancellationNotesRef.current = value;
                      }}
                      placeholder="Enter the reason for cancellation..."
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                      disabled={isUpdating}
                    />
                    <p className="mt-2 text-sm text-gray-500">
                      This information will be saved with the appointment record.
                    </p>
                  </div>

                  {/* Send cancellation email to customer */}
                  <div>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sendCancellationEmail}
                        onChange={(e) => {
                          const value = e.target.checked;
                          setSendCancellationEmail(value);
                          sendCancellationEmailRef.current = value;
                        }}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                        disabled={isUpdating}
                      />
                      <span className="text-sm font-medium text-gray-900">Send cancellation email to customer</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1 ml-6">
                      Check this to notify the customer by email; leave unchecked to cancel without sending an email.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 shrink-0">
              <button
                onClick={() => {
                  setConfirmationDialog(null);
                  setCancellationNotes('');
                  setNoShow(false);
                  setSendCancellationEmail(false);
                  setCancelScope('THIS');
                  cancelScopeRef.current = 'THIS';
                  setSeriesSummary(null);
                  cancellationNotesRef.current = '';
                  noShowRef.current = false;
                  sendCancellationEmailRef.current = false;
                }}
                disabled={isUpdating}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>{confirmationDialog.cancelText}</span>
              </button>
              <button
                onClick={confirmationDialog.onConfirm}
                disabled={isUpdating}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${confirmationDialog.confirmButtonClass || 'bg-blue-600 hover:bg-blue-700'}`}
              >
                {isUpdating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    {confirmationDialog.title === 'Confirm Appointment' && (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    {confirmationDialog.title === 'Cancel Appointment' && (
                      <Trash2 className="w-4 h-4" />
                    )}
                    {confirmationDialog.title === 'Mark as No Show' && (
                      <AlertTriangle className="w-4 h-4" />
                    )}
                    <span>{confirmationDialog.confirmText}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <MarkDepositPaidModal
        isOpen={isMarkDepositPaidModalOpen}
        depositAmount={appointment ? getDepositAmountToRecord(appointment) : 0}
        onClose={() => setIsMarkDepositPaidModalOpen(false)}
        onSubmit={handleMarkDepositPaidSubmit}
      />

      <DepositPaymentDetailsModal
        isOpen={isDepositPaymentDetailsModalOpen}
        appointmentId={appointment?.id}
        onClose={() => setIsDepositPaymentDetailsModalOpen(false)}
      />

      {/* Notes Modal */}
      {isNotesModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {appointment?.notes ? 'Edit Notes' : 'Add Notes'}
              </h2>
              <button
                onClick={handleCloseNotesModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={isSavingNotes}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="notes-textarea" className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    id="notes-textarea"
                    value={notesText}
                    onChange={(e) => setNotesText(e.target.value)}
                    placeholder="Enter notes about this appointment..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                    rows={8}
                    disabled={isSavingNotes}
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    Add any additional information or notes about this appointment.
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={handleCloseNotesModal}
                disabled={isSavingNotes}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNotes}
                disabled={isSavingNotes}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSavingNotes ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Notes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Modal */}
      {isAssignmentModalOpen && selectedAssignment && appointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-xl font-semibold text-gray-900">
                Assign Professional
              </h2>
              <button
                onClick={() => {
                  setIsAssignmentModalOpen(false);
                  setSelectedAssignment(null);
                  setSelectedProfessionalId('');
                  setAssignmentCapabilities({});
                  setAssignmentSchedules({});
                  setAssignmentExistingAppointments({});
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={isSavingAssignment}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Customer:</strong> {appointment.customer_first_name} {appointment.customer_last_name}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Service:</strong> {selectedAssignment.serviceName}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Date & Time:</strong> {(() => {
                    const { dateStr, timeStr } = getAppointmentDateAndTimeDisplay();
                    return dateStr === 'Date TBD' ? 'Date TBD' : `${dateStr} ${timeStr}`;
                  })()}
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assign Professional
                  </label>
                  {loadingAssignmentData ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                      <span className="ml-2 text-sm text-gray-500">Loading professionals...</span>
                    </div>
                  ) : assignmentProfessionals.length === 0 ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-500">
                      No professionals available for this business
                    </div>
                  ) : (
                    <select
                      value={selectedProfessionalId}
                      onChange={(e) => setSelectedProfessionalId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={isSavingAssignment}
                    >
                      <option value="">No assignment</option>
                      {assignmentProfessionals.map((professional: any) => {
                        const assignmentDisplayDate = appointment.scheduledAt ?? appointment.flexScheduledAt?.earliest;
                        const hasCapability = hasServiceCapability(professional.id, selectedAssignment.serviceId);
                        const hasConflict = assignmentDisplayDate ? hasScheduleConflict(professional.id, assignmentDisplayDate, selectedAssignment.durationMinutes) : false;
                        const warnings = [];
                        if (!hasCapability) warnings.push('No capability');
                        if (hasConflict) warnings.push('Schedule conflict');
                        
                        return (
                          <option key={professional.id} value={professional.id}>
                            {professional.firstName} {professional.lastName.charAt(0)}.
                            {warnings.length > 0 && ` (${warnings.join(', ')})`}
                          </option>
                        );
                      })}
                    </select>
                  )}
                    <div className="mt-2 space-y-1">
                    {selectedProfessionalId && assignmentProfessionals.find((p: any) => p.id === selectedProfessionalId) && (() => {
                      const professional = assignmentProfessionals.find((p: any) => p.id === selectedProfessionalId);
                      const assignmentDisplayDate = appointment.scheduledAt ?? appointment.flexScheduledAt?.earliest;
                      const hasCapability = hasServiceCapability(professional.id, selectedAssignment.serviceId);
                      const hasConflict = assignmentDisplayDate ? hasScheduleConflict(professional.id, assignmentDisplayDate, selectedAssignment.durationMinutes) : false;
                      
                      return (
                        <div key={professional.id} className="flex flex-wrap gap-2 text-xs">
                          {!hasCapability && (
                            <div className="flex items-center gap-1 text-yellow-600">
                              <AlertTriangle className="w-3 h-3" />
                              <span>No capability for this service</span>
                            </div>
                          )}
                          {hasConflict && (
                            <div className="flex items-center gap-1 text-red-600">
                              <Clock className="w-3 h-3" />
                              <span>Schedule conflict</span>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  {selectedAssignment.professionalFirstName && selectedAssignment.professionalLastName && (
                    <p className="text-xs text-gray-500 mt-1">
                      Currently assigned: {selectedAssignment.professionalFirstName} {selectedAssignment.professionalLastName.charAt(0)}.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 sticky bottom-0 bg-white">
              <button
                onClick={() => {
                  setIsAssignmentModalOpen(false);
                  setSelectedAssignment(null);
                  setSelectedProfessionalId('');
                  setAssignmentCapabilities({});
                  setAssignmentSchedules({});
                  setAssignmentExistingAppointments({});
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={isSavingAssignment}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAssignment}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSavingAssignment}
              >
                {isSavingAssignment ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </div>
                ) : (
                  'Save Assignment'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recurring series — all visits in series */}
      {isSeriesListModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[85] flex items-center justify-center p-4"
          role="presentation"
          onClick={() => setIsSeriesListModalOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[min(85vh,560px)] flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-labelledby="series-list-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-200 flex-shrink-0">
              <h2 id="series-list-title" className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#0B4778]/10">
                  <Repeat2 className="w-5 h-5 text-[#0B4778]" />
                </div>
                <span>Series Appointments</span>
              </h2>
              <button
                type="button"
                onClick={() => setIsSeriesListModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 min-h-0">
              {seriesListLoading ? (
                <div className="flex justify-center py-12">
                  <LoadingSpinner />
                </div>
              ) : seriesListItems.length === 0 ? (
                <p className="text-sm text-gray-600 text-center py-8">No other appointments found in this series.</p>
              ) : (
                <ul className="space-y-2">
                  {seriesListItems.map((row) => {
                    const isCurrent = String(row.id) === String(appointmentId);
                    const whenLabel = row.scheduledAt
                      ? `${formatDateOnly(row.scheduledAt)} · ${formatTimeOnly(row.scheduledAt)}`
                      : row.flexScheduledAt?.earliest
                        ? `${formatDateOnly(row.flexScheduledAt.earliest)} · ${formatTimeOnly(row.flexScheduledAt.earliest)} – ${formatTimeOnly(row.flexScheduledAt.latest)}`
                        : '—';
                    return (
                      <li key={row.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setIsSeriesListModalOpen(false);
                            navigate(`/appointments/${row.id}${routerLocation.search || ''}`);
                          }}
                          className={`w-full text-left rounded-lg border px-3 py-3 transition-colors ${
                            isCurrent
                              ? 'border-[#0B4778] bg-[#0B4778]/5 ring-1 ring-[#0B4778]/30'
                              : 'border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-semibold text-gray-900">
                              {row.occurrenceIndex != null ? `Visit ${row.occurrenceIndex}` : 'Visit'}
                            </span>
                            <span
                              className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                                row.status === 'CONFIRMED'
                                  ? 'bg-blue-50 text-blue-800 border-blue-200'
                                  : row.status === 'PENDING' || row.status === 'PENDING_QUOTE'
                                    ? 'bg-amber-50 text-amber-800 border-amber-200'
                                    : row.status === 'COMPLETED'
                                      ? 'bg-green-50 text-green-800 border-green-200'
                                      : row.status === 'CANCELLED'
                                        ? 'bg-red-50 text-red-800 border-red-200'
                                        : 'bg-gray-50 text-gray-700 border-gray-200'
                              }`}
                            >
                              {formatAppointmentStatusLabel(row.status)}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 mt-1">{whenLabel}</div>
                          {isCurrent && <div className="text-xs text-[#0B4778] font-medium mt-1">Current appointment</div>}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end flex-shrink-0">
              <button
                type="button"
                onClick={() => setIsSeriesListModalOpen(false)}
                className="aacore-btn2-secondary"
              >
                <X className="w-4 h-4" />
                <span>Close</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Duration Modal */}
      {isDurationModalOpen && durationEditing && appointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[80] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#0B4778]/10">
                  <CalendarClock className="w-4 h-4 text-[#0B4778]" />
                </div>
                <span>Edit Duration</span>
              </h2>
              <button
                onClick={closeDurationModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={isSavingDuration}
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="text-sm">
                <div className="font-semibold text-gray-900">{durationEditing.serviceName}</div>
                <div className="text-gray-600 mt-1 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>
                    Starts {new Date(durationEditing.startIso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                    {' • '}
                    Ends{' '}
                    {(() => {
                      const h = Number(durationHours) || 0;
                      const m = Number(durationMinutes) || 0;
                      const total = h * 60 + m;
                      const end = new Date(new Date(durationEditing.startIso).getTime() + total * 60 * 1000);
                      return end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                    })()}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hours</label>
                  <select
                    value={durationHours}
                    onChange={(e) => setDurationHours(e.target.value)}
                    disabled={isSavingDuration}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] bg-white"
                  >
                    {Array.from({ length: 13 }, (_, i) => String(i)).map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Minutes</label>
                  <select
                    value={durationMinutes}
                    onChange={(e) => setDurationMinutes(e.target.value)}
                    disabled={isSavingDuration}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] bg-white"
                  >
                    {['00', '15', '30', '45'].map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="text-xs text-gray-500">
                Duration changes are saved immediately and will update the assignment end time.
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
              <button
                type="button"
                onClick={closeDurationModal}
                disabled={isSavingDuration}
                className="aacore-btn2-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="w-4 h-4" />
                <span>Cancel</span>
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!appointment || !durationEditing) return;
                  const h = Number(durationHours) || 0;
                  const m = Number(durationMinutes) || 0;
                  const total = h * 60 + m;
                  if (total < 15) {
                    toast.error('Duration must be at least 15 minutes');
                    return;
                  }
                  if (total % 15 !== 0) {
                    toast.error('Minutes must be 0, 15, 30, or 45');
                    return;
                  }
                  setIsSavingDuration(true);
                  try {
                    await apiService.updateAssignmentDuration({
                      appointmentId: appointment.id,
                      assignmentId: durationEditing.assignmentId,
                      durationMinutes: total
                    });
                    const updatedAppointment = await apiService.getAppointment(appointment.id);
                    setAppointment(updatedAppointment);
                    refreshActivityEvents();
                    toast.success('Duration updated');
                    closeDurationModal();
                  } catch (error: any) {
                    console.error('Error updating duration:', error);
                    toast.error(error.response?.data?.message || 'Failed to update duration');
                  } finally {
                    setIsSavingDuration(false);
                  }
                }}
                disabled={isSavingDuration}
                className="aacore-btn2-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingDuration ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Save</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentDetailsPage; 